// Client-side Oracle schema cache, keyed by connection and scoped to one
// SCHEMA (Oracle's session schema is the renderer's "database" for a
// connection).
//
// TIERED LOAD (SLICES.md § S7 items 1-4, 7-8 — the MSSQL design of
// `Docs/IntelliSenseSnapshotLoad.md` applied to oracle, the largest of the
// five data dictionaries):
//
//   phase 1 — the relation list (`ALL_OBJECTS`), the accessible schema list
//             and the `product_component_version` probe. Published to the
//             schema bus the moment it lands, which flips the IntelliSense
//             pill to ready and makes relation-name completion work.
//             `ensureOracleSchema` resolves here.
//   phase 2 — PK flags + columns, CHECK clauses, FK edges, sequences,
//             synonyms, routines and their signatures, packages and their
//             members, user types and OBJECT-type attributes, and (23ai+)
//             property graphs — merged page by page into the SAME snapshot
//             object and re-published as they arrive
//             (`whenOracleSnapshotComplete` awaits it).
//
// ORACLE DIVERGENCE — the row-limiting clause `OFFSET … ROWS FETCH NEXT …
// ROWS ONLY` only exists on 12.1+. Phase 1 probes the version first and every
// phase-2 fetch is told whether it may page; on an older release each artefact
// falls back to ONE query capped at `ORACLE_LEGACY_MAX_ROWS` (50,000) that
// reports its own truncation, which is exactly the pre-S7 behaviour.

import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { projectOracleSnapshot } from '@/features/intellisense/schemaBus/adapters/oracleAdapter';

import { INTROSPECTION_PAGE_ROWS, isCompleteOutcome, yieldToEventLoop } from './introspectionPaging';
import {
  fetchOracleCheckConstraintPages,
  fetchOracleColumnPages,
  fetchOracleForeignKeyPages,
  fetchOraclePackages,
  fetchOraclePrimaryKeys,
  fetchOraclePropertyGraphs,
  fetchOracleRelationPages,
  fetchOracleRoutinePages,
  fetchOracleSchemaNames,
  fetchOracleSequencePages,
  fetchOracleServerVersion,
  fetchOracleSynonyms,
  fetchOracleTypeAttributePages,
  fetchOracleUserTypePages,
  oracleSupportsRowLimiting,
  type OraclePagingMode,
  type OracleRelationEntry,
} from './oracleSchemaIntrospection';
import { fetchOracleRoutineParams } from './oracleRoutineIntrospection';
import type { OracleSchemaColumn, OracleSchemaSnapshot } from './oracleSchemaTypes';
import {
  applyOracleCheckPage,
  applyOracleColumnPage,
  applyOracleForeignKeyPage,
  applyOracleRoutinePage,
  applyOracleRoutineParams,
  applyOracleSequencePage,
  applyOracleSynonymPage,
  applyOracleTypeAttributePage,
  applyOracleUserTypePage,
  carryOverOracleIntrospection,
  createOracleLiveSnapshot,
  reconcileOracleChecks,
  setOraclePackages,
  setOraclePropertyGraphs,
  type OracleLiveSnapshot,
} from './oracleSnapshotMerge';
import { beginIntrospectionLoad } from './introspectionTruncation';
import { SNAPSHOT_STALE_AFTER_MS } from './snapshotLifetime';
import {
  createLoadFailures,
  createTieredCacheEntry,
  isEntryFresh,
  markLoadOutcome,
  publishDegraded,
  publishThrottled,
  recordLoadFailure,
  reportPartialSnapshot,
  whenEntryColumnsLoaded,
  whenEntryComplete,
  type LoadFailures,
  type TieredCacheEntry,
} from './tieredSnapshotCache';

type CacheEntry = TieredCacheEntry<OracleSchemaSnapshot>;

const cacheByConnectionId = new Map<string, CacheEntry>();
// S7 item 3 — the snapshot is built once per connection and rebuilt only on
// DDL invalidation, explicit `forceRefresh`, or reconnect. This is a
// day-scale backstop, NOT a refresh cadence (was 30_000, which re-ran the
// whole data-dictionary fan-out while the user was editing).
const DEFAULT_TTL_MS = SNAPSHOT_STALE_AFTER_MS;

function entryFor(connectionId: string): CacheEntry {
  const existing = cacheByConnectionId.get(connectionId);
  if (existing) return existing;
  const created = createTieredCacheEntry<OracleSchemaSnapshot>();
  cacheByConnectionId.set(connectionId, created);
  return created;
}

export function getCachedOracleSchema(connectionId: string): OracleSchemaSnapshot | undefined {
  return cacheByConnectionId.get(connectionId)?.snapshot;
}

export function invalidateOracleSchema(connectionId: string): void {
  const entry = cacheByConnectionId.get(connectionId);
  if (!entry) return;
  // Bump the generation so any refresh already in flight cannot publish its
  // pre-invalidation snapshot once it resolves — and so an in-flight phase 2
  // stops merging AND stops paging.
  entry.generation += 1;
  entry.refreshPromise = undefined;
  // Soft invalidation: keep serving the last snapshot but mark it stale so the
  // next ensure() bypasses the TTL fast-path and refreshes.
  if (entry.snapshot) entry.snapshot = { ...entry.snapshot, builtAt: 0 };
}

export function dropOracleSchema(connectionId: string): void {
  // Mutate the entry in place (rather than deleting the map key) so a refresh
  // whose closure still holds this entry object observes the bumped generation
  // and skips its stale publish/cache.
  const entry = cacheByConnectionId.get(connectionId);
  if (!entry) return;
  entry.generation += 1;
  entry.snapshot = undefined;
  entry.refreshPromise = undefined;
  entry.phase2 = undefined;
  entry.columnsLoaded = undefined;
}

/**
 * Test seam — seed the native Oracle schema cache synchronously without
 * standing up an Electron bridge. Used by the dialect-native Oracle
 * resolver tests to inject a synonym list (the bus snapshot adapter does
 * not project synonyms today). Gated on test mode (`SQL_STUDIO_TEST=1` or
 * `NODE_ENV=test`).
 */
export function injectOracleSchemaForTest(
  connectionId: string,
  snapshot: OracleSchemaSnapshot,
): void {
  if (typeof process !== 'undefined') {
    const isTest = process.env?.SQL_STUDIO_TEST === '1' || process.env?.NODE_ENV === 'test';
    if (!isTest) {
      throw new Error(
        'injectOracleSchemaForTest is only available in test mode (SQL_STUDIO_TEST=1 or NODE_ENV=test).',
      );
    }
  }
  const entry = entryFor(connectionId);
  entry.snapshot = snapshot;
  entry.lastError = undefined;
}

export function __resetOracleSchemaCacheForTest(): void {
  cacheByConnectionId.clear();
}

/**
 * Phase 1 — the relation list, the schema list and the version probe.
 * Publishes the moment it lands and kicks phase 2 without awaiting it.
 *
 * The version probe runs FIRST and alone: its answer decides whether every
 * later query may carry a row-limiting clause at all.
 */
async function runOraclePhase1(
  entry: CacheEntry,
  options: { connectionId: string; schema: string },
): Promise<OracleSchemaSnapshot> {
  const { connectionId, schema } = options;
  const generation = entry.generation + 1;
  entry.generation = generation;
  const failures = createLoadFailures();
  const shouldAbort = (): boolean => entry.generation !== generation;
  // The record describes THIS load, not the previous one (R3).
  beginIntrospectionLoad(connectionId, schema);

  const serverVersion = await fetchOracleServerVersion(connectionId, schema).catch((error: unknown) => {
    recordLoadFailure(failures, 'serverinfo', error);
    return '';
  });
  const mode: OraclePagingMode = {
    supportsRowLimiting: oracleSupportsRowLimiting(serverVersion),
    shouldAbort,
  };

  const relations: OracleRelationEntry[] = [];
  let relationsFailed = false;
  const [, schemas] = await Promise.all([
    fetchOracleRelationPages(
      connectionId,
      schema,
      (page) => {
        relations.push(...page);
      },
      mode,
    ).catch((error: unknown) => {
      relationsFailed = true;
      recordLoadFailure(failures, 'relations', error);
    }),
    fetchOracleSchemaNames(connectionId, schema, mode).catch((error: unknown) => {
      recordLoadFailure(failures, 'schemas', error);
      return [] as string[];
    }),
  ]);

  // Stale-while-revalidate carries the previous refresh's payload forward —
  // but only within the SAME scope. The cache is keyed by connection and a
  // schema switch lands here with the other schema's snapshot in `entry`;
  // matching its same-named tables would hand schema B the columns of A.T
  // until phase 2 replaces them (release review 1.5.0, F4).
  const previous = entry.snapshot?.schema === schema ? entry.snapshot : undefined;

  // Stale-while-revalidate: keep the previous refresh's columns / CHECK
  // clauses on relations that still exist until phase 2 replaces them.
  const objects = carryOverOracleIntrospection(
    previous ? previous.tables.concat(previous.views) : undefined,
    relations,
  );

  const snapshot: OracleSchemaSnapshot = {
    connectionId,
    schema,
    builtAt: Date.now(),
    serverVersion,
    tables: objects.filter((o) => o.type === 'table'),
    views: objects.filter((o) => o.type !== 'table'),
    sequences: previous?.sequences ?? [],
    synonyms: previous?.synonyms ?? [],
    routines: previous?.routines ?? [],
    packages: previous?.packages ?? [],
    schemas,
    objectsByNameLower: {},
    columnsByObjectNameLower: {},
    columnIndexByNameLower: {},
    routinesByNameLower: previous?.routinesByNameLower ?? {},
    foreignKeys: previous?.foreignKeys ?? [],
    userTypes: previous?.userTypes ?? [],
    typeAttributesByTypeName: previous?.typeAttributesByTypeName ?? {},
    propertyGraphs: previous?.propertyGraphs ?? [],
    phase: 'tables',
  };

  if (relationsFailed) {
    return publishDegraded(entry, generation, {
      previous,
      built: snapshot,
      failures,
      engine: 'oracle',
      project: projectOracleSnapshot,
    });
  }

  const live = await createOracleLiveSnapshot(snapshot);
  // A drop / newer refresh landed while we were projecting — discard.
  if (entry.generation !== generation) return snapshot;

  entry.snapshot = snapshot;
  entry.lastError = undefined;
  publishThrottled(entry, live.projected, true);
  entry.phase2 = runOraclePhase2(entry, live, generation, failures, mode);
  return snapshot;
}

/**
 * Phase 2 — every per-relation and per-schema artefact, paged (or capped, on a
 * pre-12c server), merged incrementally and re-published as pages land. Never
 * rejects: an artefact that fails degrades to whatever pages already merged
 * and is named in the `feature:schema_snapshot_partial` event.
 */
async function runOraclePhase2(
  entry: CacheEntry,
  live: OracleLiveSnapshot,
  generation: number,
  failures: LoadFailures,
  mode: OraclePagingMode,
): Promise<void> {
  const { connectionId, schema } = live.native;
  const superseded = (): boolean => entry.generation !== generation;

  const onPage = (merge: () => void): void => {
    if (superseded()) return;
    merge();
    publishThrottled(entry, live.projected, false);
  };

  const step = async (name: string, run: () => Promise<void>): Promise<void> => {
    if (superseded()) return;
    try {
      await run();
    } catch (error) {
      recordLoadFailure(failures, name, error);
    }
    if (!superseded()) publishThrottled(entry, live.projected, false);
  };

  // PK flags must exist before the first column page is aggregated, so the
  // pair is ONE step rather than two racing ones. Exposed as `columnsLoaded`
  // (set before this function first yields) so the Visual Query Editor can
  // start editing without waiting for the other steps.
  const columns = step('columns', async () => {
    const primaryKeys = await fetchOraclePrimaryKeys(connectionId, schema, mode);
    if (superseded()) return;
    await fetchOracleColumnPages(
      connectionId,
      schema,
      primaryKeys,
      (page) => onPage(() => applyOracleColumnPage(live, page)),
      mode,
    );
  });
  entry.columnsLoaded = columns;
  await Promise.all([
    columns,
    step('checks', async () => {
      const outcome = await fetchOracleCheckConstraintPages(
        connectionId,
        schema,
        (page) => onPage(() => applyOracleCheckPage(live, page)),
        mode,
      );
      // Only a COMPLETE read may clear carried-over clauses on untouched
      // relations; a capped or aborted one proves nothing about them.
      if (isCompleteOutcome(outcome)) onPage(() => reconcileOracleChecks(live));
    }),
    step('fk', () =>
      fetchOracleForeignKeyPages(
        connectionId,
        schema,
        (page) => onPage(() => applyOracleForeignKeyPage(live, page)),
        mode,
      ),
    ),
    step('sequences', () =>
      fetchOracleSequencePages(
        connectionId,
        schema,
        (page) => onPage(() => applyOracleSequencePage(live, page)),
        mode,
      ),
    ),
    step('synonyms', async () => {
      const synonyms = await fetchOracleSynonyms(connectionId, schema, mode);
      onPage(() => applyOracleSynonymPage(live, synonyms));
    }),
    step('routines', () =>
      fetchOracleRoutinePages(
        connectionId,
        schema,
        (page) => onPage(() => applyOracleRoutinePage(live, page)),
        mode,
      ),
    ),
    step('routine-params', async () => {
      const byName = await fetchOracleRoutineParams(connectionId, schema, mode);
      onPage(() => applyOracleRoutineParams(live, byName));
    }),
    step('packages', async () => {
      const packages = await fetchOraclePackages(connectionId, schema, mode);
      onPage(() => setOraclePackages(live, packages));
    }),
    step('types', () =>
      fetchOracleUserTypePages(
        connectionId,
        schema,
        (page) => onPage(() => applyOracleUserTypePage(live, page)),
        mode,
      ),
    ),
    step('type-attrs', () =>
      fetchOracleTypeAttributePages(
        connectionId,
        schema,
        (page) => onPage(() => applyOracleTypeAttributePage(live, page)),
        mode,
      ),
    ),
    step('graphs', async () => {
      const graphs = await fetchOraclePropertyGraphs(connectionId, schema);
      onPage(() => setOraclePropertyGraphs(live, graphs));
    }),
  ]);

  if (superseded()) return;
  live.native.phase = 'complete';
  markLoadOutcome(entry, failures.missing, { connectionId, database: schema });
  publishThrottled(entry, live.projected, true);
  reportPartialSnapshot('oracle', failures);
}

export async function ensureOracleSchema(options: {
  connectionId: string;
  schema: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<OracleSchemaSnapshot> {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const entry = entryFor(options.connectionId);

  // The cache is keyed by connection, but a snapshot describes ONE schema.
  // With the day-scale TTL a schema switch would otherwise keep serving the
  // previous schema's objects forever, so a scope mismatch counts as a miss.
  const scopeMatches = entry.snapshot?.schema === options.schema;

  if (
    !options.forceRefresh
    && entry.snapshot
    && scopeMatches
    && isEntryFresh(entry, ttlMs)
  ) {
    // The bus is cleared independently of this cache — by the DDL invalidator
    // and by disconnect/reconnect (`invalidateConnection`). Re-publish here
    // whenever the bus lost the entry, or the resolver would read a cold bus
    // forever.
    if (!getSchemaBusSnapshot(options.connectionId, options.schema)) {
      publishThrottled(entry, projectOracleSnapshot(entry.snapshot), true);
    }
    return entry.snapshot;
  }

  // Dedup only a refresh of the SAME schema. Handing a request for schema B
  // the in-flight promise for schema A resolves B's caller with A's snapshot
  // (release review 1.5.0, F4); starting B's refresh instead bumps the
  // generation, so A's late publish is discarded by the guards below.
  if (entry.refreshPromise && entry.refreshScope === options.schema) {
    return entry.refreshPromise;
  }

  const refresh = runOraclePhase1(entry, options)
    .catch((error: unknown) => {
      entry.lastError = error instanceof Error ? error.message : 'Failed to load Oracle schema';
      throw error;
    })
    .finally(() => {
      // Only clear OUR in-flight marker: a drop/invalidate may have handed
      // `refreshPromise` to a newer refresh while we were in flight.
      if (entry.refreshPromise === refresh) {
        entry.refreshPromise = undefined;
        entry.refreshScope = undefined;
      }
    });
  entry.refreshPromise = refresh;
  entry.refreshScope = options.schema;
  return refresh;
}

/**
 * Resolve once the snapshot for `connectionId` is fully loaded — phase 1
 * published AND phase 2 finished merging. Never rejects.
 */
export async function whenOracleSnapshotComplete(connectionId: string): Promise<void> {
  await whenEntryComplete(cacheByConnectionId.get(connectionId));
}

/**
 * Resolve once the snapshot's columns have loaded (phase 1 + the phase-2
 * column step), without waiting for CHECK clauses, FK edges, sequences,
 * synonyms, routines, packages, types or property graphs — see
 * `whenEntryColumnsLoaded`.
 */
export async function whenOracleColumnsLoaded(connectionId: string): Promise<void> {
  await whenEntryColumnsLoaded(cacheByConnectionId.get(connectionId));
}

/** `ensureOracleSchema` + `whenOracleColumnsLoaded` — the Visual Query
 *  Editor's live schema pull: editable once tables + columns are in; the later
 *  artefacts arrive through the schema bus. */
export async function ensureOracleSchemaWithColumns(options: {
  connectionId: string;
  schema: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<OracleSchemaSnapshot> {
  const snapshot = await ensureOracleSchema(options);
  await whenOracleColumnsLoaded(options.connectionId);
  return getCachedOracleSchema(options.connectionId) ?? snapshot;
}

/** `ensureOracleSchema` + `whenOracleSnapshotComplete` — for callers that need
 *  the WHOLE snapshot (the coverage harness's post-DDL hook, the pill's
 *  "Reload schema" action). */
export async function ensureOracleSchemaComplete(options: {
  connectionId: string;
  schema: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<OracleSchemaSnapshot> {
  const snapshot = await ensureOracleSchema(options);
  await whenOracleSnapshotComplete(options.connectionId);
  return getCachedOracleSchema(options.connectionId) ?? snapshot;
}

/**
 * Test seam for the long-task perf gate: build a synthetic dictionary of
 * `tableCount` relations x `columnsPerTable` columns and push it through the
 * REAL merge path — the same chunked `createOracleLiveSnapshot` projection,
 * the same `applyOracleColumnPage` merge in `INTROSPECTION_PAGE_ROWS`-sized
 * pages, the same throttled bus publishes.
 */
export async function injectSyntheticOracleSnapshotForTest(options: {
  connectionId: string;
  schema: string;
  tableCount: number;
  columnsPerTable: number;
}): Promise<void> {
  const entry = entryFor(options.connectionId);
  entry.generation += 1;

  const tables = [];
  for (let i = 0; i < options.tableCount; i += 1) {
    tables.push({
      name: `SYNTHETIC_TABLE_${i}`,
      schema: options.schema,
      type: 'table' as const,
      columns: [] as OracleSchemaColumn[],
    });
    if (tables.length % INTROSPECTION_PAGE_ROWS === 0) await yieldToEventLoop();
  }

  const snapshot: OracleSchemaSnapshot = {
    connectionId: options.connectionId,
    schema: options.schema,
    builtAt: Date.now(),
    serverVersion: '19.0.0.0.0',
    tables,
    views: [],
    sequences: [],
    synonyms: [],
    routines: [],
    packages: [],
    schemas: [options.schema],
    objectsByNameLower: {},
    columnsByObjectNameLower: {},
    columnIndexByNameLower: {},
    routinesByNameLower: {},
    foreignKeys: [],
    userTypes: [],
    typeAttributesByTypeName: {},
    propertyGraphs: [],
    phase: 'tables',
  };
  const live = await createOracleLiveSnapshot(snapshot);
  entry.snapshot = snapshot;
  publishThrottled(entry, live.projected, true);

  let page: { table: string; item: OracleSchemaColumn }[] = [];
  const flush = async (): Promise<void> => {
    if (page.length === 0) return;
    applyOracleColumnPage(live, page);
    page = [];
    publishThrottled(entry, live.projected, false);
    await yieldToEventLoop();
  };
  for (let t = 0; t < options.tableCount; t += 1) {
    for (let c = 0; c < options.columnsPerTable; c += 1) {
      page.push({
        table: `SYNTHETIC_TABLE_${t}`,
        item: {
          name: `COL_${c}`,
          dataType: 'VARCHAR2(100)',
          isNullable: true,
          isPrimaryKey: c === 0,
          ordinalPosition: c + 1,
        },
      });
      if (page.length >= INTROSPECTION_PAGE_ROWS) await flush();
    }
  }
  await flush();
  live.native.phase = 'complete';
  publishThrottled(entry, live.projected, true);
}
